import { useState, useEffect } from 'react';
import api, { handleApiError } from '@/services/api';
import { getRepliesFromCache, saveRepliesToCache, updateReplyInCache } from '@/utils/replyStorage';
import { mergeEmailBody } from '@/utils/emailBody';

export interface EmailReply {
  id: string;
  uid: number;
  seqno: number;
  flags: string[];
  size: number;
  replyCount: number;
  envelope: {
    date: string;
    subject: string;
    from: Array<{ name: string; mailbox: string; host: string }>;
    sender: Array<{ name: string; mailbox: string; host: string }>;
    replyTo: Array<{ name: string; mailbox: string; host: string }>;
    to: Array<{ name: string | null; mailbox: string; host: string }>;
    cc: Array<{ name: string; mailbox: string; host: string }> | null;
    bcc: Array<{ name: string; mailbox: string; host: string }> | null;
    inReplyTo: string | null;
    messageId: string;
  };
  header: any;
  body: {
    html: string;
    text: string;
    raw: string;
  };
  attachments: Array<{
    filename: string;
    contentType: string;
    size: number;
    content?: string;
    public_url?: string;
  }>;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalPages: number;
  totalItems: number;
}

interface RepliesResponse {
  messages: EmailReply[];
  pagination: PaginationInfo;
}

export const useEmailReplies = (emailId: string, uid: string, refreshKey: number = 0) => {
  const [replies, setReplies] = useState<EmailReply[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalItems: 0,
  });
  const [hasMore, setHasMore] = useState(false);

  const fetchReplies = async (page: number = 1) => {
    if (!emailId || !uid) return;

    let shouldFetchFromServer = true;
    let initialDataSet = false;

    // Check cache first for initial page
    if (page === 1) {
      const cached = getRepliesFromCache(emailId);
      if (cached) {
        const cleanedCachedReplies = cached.replies.map(reply => ({
          ...reply,
          body: mergeEmailBody(reply.body, null) as EmailReply['body'],
        }));
        setReplies(cleanedCachedReplies);
        setPagination({
          page: cached.pagination.page,
          limit: pagination.limit,
          totalPages: cached.pagination.totalPages,
          totalItems: cached.pagination.total
        });
        setHasMore(cached.pagination.hasNext);
        initialDataSet = true;
        
        // If cache is fresh (less than 30 seconds old), don't fetch from server
        if (Date.now() - cached.timestamp < 30000) {
          shouldFetchFromServer = false;
        }
      }
    }

    if (!shouldFetchFromServer) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.get<RepliesResponse>(`/api/email/replies/${emailId}/${uid}`, {
        params: {
          page,
          limit: pagination.limit,
        },
      });

      const cached = page === 1 ? getRepliesFromCache(emailId) : null;
      const newReplies = response.data.messages.map(reply => {
        // Detail/replies APIs can return body "0"; keep any previously cached usable body
        const previous = cached?.replies.find(item => item.id === reply.id);
        return {
          ...reply,
          body: mergeEmailBody(reply.body, previous?.body) as EmailReply['body'],
        };
      });
      
      if (page === 1) {
        // Only update state if data is different from cache
        if (!initialDataSet || JSON.stringify(newReplies) !== JSON.stringify(replies)) {
          setReplies(newReplies);
          setPagination(response.data.pagination);
          setHasMore(response.data.pagination.page < response.data.pagination.totalPages);
        }
        
        // Always update cache with fresh data
        saveRepliesToCache(emailId, newReplies, {
          page: response.data.pagination.page,
          totalPages: response.data.pagination.totalPages,
          hasNext: response.data.pagination.page < response.data.pagination.totalPages,
          hasPrev: response.data.pagination.page > 1,
          total: response.data.pagination.totalItems
        });
      } else {
        // For pagination, append new replies
        setReplies(prev => {
          // Filter out duplicates based on reply ID
          const existingIds = new Set(prev.map(reply => reply.id));
          const uniqueNewReplies = newReplies.filter(reply => !existingIds.has(reply.id));
          return [...prev, ...uniqueNewReplies];
        });
        setPagination(response.data.pagination);
        setHasMore(response.data.pagination.page < response.data.pagination.totalPages);
      }

      setError(null);

      // Mark unread replies as read
      const unreadReplies = response.data.messages
        .filter(reply => !reply.flags.includes('\\Seen'))
        .map(reply => reply.id);

      if (unreadReplies.length > 0) {
        try {
          await api.post('/api/email/batch', {
            folderPath: 'INBOX',
            emailIds: unreadReplies
          });
          
          // Update flags in cache for marked replies
          unreadReplies.forEach(replyId => {
            updateReplyInCache(emailId, replyId, ['\\Seen']);
          });

          // Update local state to reflect read status
          setReplies(prev => prev.map(reply => {
            if (unreadReplies.includes(reply.id)) {
              return {
                ...reply,
                flags: [...new Set([...reply.flags, '\\Seen'])]
              };
            }
            return reply;
          }));
        } catch (err) {
          console.error('Error marking replies as read:', err);
        }
      }
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasMore || loading) return;
    await fetchReplies(pagination.page + 1);
  };

  // Reset pagination and fetch replies when emailId, uid, or refreshKey changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchReplies(1);
  }, [emailId, uid, refreshKey]);

  return {
    replies,
    loading,
    error,
    pagination,
    hasMore,
    loadMore,
    refresh: () => fetchReplies(1), // Add a refresh function
  };
}; 